import axios from "axios";

const host = window.location.hostname;
const envApiBase = process.env.REACT_APP_API_BASE;
const envWsBase = process.env.REACT_APP_WS_BASE;

let API_BASE;
let WS_BASE;
if (envApiBase) {
  API_BASE = envApiBase;
  WS_BASE = envWsBase || envApiBase.replace(/^http/, "ws").replace(/\/api\/?$/, "/api/mt5/ws");
} else if (host.includes("devtunnels.ms")) {
  const match = host.match(/^(.+)-\d+\.(.+)$/);
  if (!match) throw new Error(`Invalid DevTunnel hostname: ${host}`);
  const backendHost = `${match[1]}-8000.${match[2]}`;
  API_BASE = `https://${backendHost}/api`;
  WS_BASE = `wss://${backendHost}/api/mt5/ws`;
} else if (host === "localhost" || host === "127.0.0.1") {
  API_BASE = "http://localhost:8001/api";
  WS_BASE = "ws://localhost:8001/api/mt5/ws";
} else if (host === "edgehubai.wilmatrose.workers.dev" || host.endsWith("-edgehubai.wilmatrose.workers.dev")) {
  API_BASE = "https://edgehub.onrender.com/api";
  WS_BASE = "wss://edgehub.onrender.com/api/mt5/ws";
} else {
  const protocol = window.location.protocol === "https:" ? "https" : "http";
  const wsProtocol = protocol === "https" ? "wss" : "ws";
  API_BASE = `${protocol}://${host}:8001/api`;
  WS_BASE = `${wsProtocol}://${host}:8001/api/mt5/ws`;
}

const API = axios.create({ baseURL: API_BASE, timeout: 30000 });

// Backend root without the /api suffix (uploads + the cheap /health probe live there)
const BACKEND_BASE = API_BASE.replace(/\/api\/?$/, "");

// Render free tier sleeps after ~15 min idle; the next request waits 30-60s+ for
// a cold start, which used to blow past axios' 30s cap on login. Fire a cheap,
// silent ping at the earliest possible moment (module load) so the wake-up
// overlaps with page load and typing instead of blocking the first real call.
export function warmUpBackend() {
  try {
    fetch(`${BACKEND_BASE}/health`, { cache: "no-store" }).catch(() => {});
  } catch {
    /* never surface warm-up failures */
  }
}
warmUpBackend();

// Helper to resolve upload URLs against the API base
// Backend may return relative URLs like "/uploads/filename.jpg"
export const resolveUploadUrl = (url) => {
  if (!url) return url;
  // Already absolute URL
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  // Inline data (base64) or blob URLs are self-contained - never prepend a base
  // (doing so creates https://host/api/data:image/... which 404s and breaks images)
  if (url.startsWith("data:") || url.startsWith("blob:")) return url;
  // Relative URL - prepend API base (strip /api suffix for uploads)
  const base = API_BASE.replace(/\/api\/?$/, "");
  return `${base}${url.startsWith("/") ? "" : "/"}${url}`;
};
API.interceptors.request.use(config => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

API.interceptors.response.use(response => response, async error => {
  const config = error.config || {};
  const url = config.url || "";
  // Never attempt a token refresh for auth endpoints themselves — otherwise a
  // failed /auth/refresh would re-enter this interceptor and loop forever.
  const isAuthCall = url.includes("/auth/refresh") || url.includes("/auth/login") || url.includes("/auth/signup");
  if (error.response?.status === 401 && !config._authRetry && !isAuthCall && localStorage.getItem("token")) {
    config._authRetry = true;
    try {
      // 60s: a cold Render instance can take longer than the default 30s to respond
      const refreshed = await API.post("/auth/refresh", null, { timeout: 60000 });
      const token = refreshed.data?.access_token;
      if (token) {
        localStorage.setItem("token", token);
        config.headers.Authorization = `Bearer ${token}`;
        return API.request(config);
      }
    } catch (refreshError) {
      console.warn("Session refresh failed", refreshError?.message);
    }
  }
  if (error.response?.status === 401) {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    if (window.location.pathname !== "/auth") window.location.assign("/auth");
  }
  return Promise.reject(error);
});

export const getCurrentUser = () => {
  const raw = localStorage.getItem("user");
  return raw ? JSON.parse(raw) : null;
};
export const clearAuthData = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  closeWS();
};
export const forgotPasswordAPI = email => API.post("/auth/forgot-password", { email });
export const verifyOtpAPI = (email, otp) => API.post("/auth/verify-otp", { email, otp });
export const resetPasswordAPI = (token, newPassword) => API.post("/auth/reset-password", { token, new_password: newPassword });
export const updateMT5Credentials = payload => API.post("/mt5/credentials", payload);
export const getMT5Credentials = () => API.get("/mt5/credentials");
export const getMT5Account = () => API.get("/mt5/account");
export const getMT5Trades = (params = {}) => API.get("/mt5/trades", { params });
export const sendMT5BridgeUpdate = (payload, secret) => API.post("/mt5/update", payload, { headers: { "x-bridge-secret": secret } });
export const getUserJournal = (params = {}) => API.get("/ai/journal/entries", { params });
export const createJournalEntry = payload => API.post("/ai/journal", payload);
export const updateJournalEntry = (entryId, payload) => API.put(`/ai/journal/${entryId}`, payload);
export const deleteJournalEntry = entryId => API.delete(`/ai/journal/${entryId}`);
export const sendAIChat = message => API.post("/ai/chat", { message });
export const getMarketOverview = (symbols, signal) => API.get("/markets/overview", { params: { symbols: symbols.join(",") }, signal });
export const getPairFundamentals = (symbol, signal) => API.get(`/markets/${encodeURIComponent(symbol)}/fundamentals`, { signal });
export const getPairBias = (symbol, signal) => API.get(`/markets/${encodeURIComponent(symbol)}/bias`, { signal });
export const getMarketAccess = () => API.get("/markets/access");
export const getMarketNews = (symbol, signal) => API.get(`/markets/${encodeURIComponent(symbol)}/news`, { signal });
export const getMarketMacro = (symbol, signal) => API.get(`/markets/${encodeURIComponent(symbol)}/macro`, { signal });
// --- Market terminal (full wiring) ---
export const getMarketInstruments = (signal) => API.get("/markets/instruments", { signal });
export const getWatchlist = () => API.get("/markets/watchlist");
export const updateWatchItem = (symbol, moveAlertPct) => API.put(`/markets/watchlist/${encodeURIComponent(symbol)}`, { move_alert_pct: moveAlertPct });
export const removeWatchItem = (symbol) => API.delete(`/markets/watchlist/${encodeURIComponent(symbol)}`);
export const getMarketCandles = (symbol, interval = "1day", points = 120, signal) => API.get(`/markets/${encodeURIComponent(symbol)}/candles`, { params: { interval, points }, signal });
export const getMarketEvents = (symbol, signal) => API.get(`/markets/${encodeURIComponent(symbol)}/events`, { signal });
export const getMarketCot = (symbol, signal) => API.get(`/markets/${encodeURIComponent(symbol)}/cot`, { signal });
export const getMarketAnalytics = (symbol, compare = "", signal) => API.get(`/markets/${encodeURIComponent(symbol)}/analytics`, { params: { compare }, signal });
export const getMarketDigest = (symbol, signal) => API.get(`/markets/${encodeURIComponent(symbol)}/digest`, { signal });
export const sendMarketChat = (symbol, message) => API.post(`/markets/${encodeURIComponent(symbol)}/chat`, { message });
export const getMarketChatHistory = (symbol) => API.get(`/markets/${encodeURIComponent(symbol)}/chat/history`);
export const clearMarketChatHistory = (symbol) => API.delete(`/markets/${encodeURIComponent(symbol)}/chat/history`);
export const getMarketZones = (symbol, interval = "15min", points = 400, minScore = 0, signal) =>
  API.get(`/markets/${encodeURIComponent(symbol)}/zones`, { params: { interval, points, min_score: minScore }, signal });
export const getMarketZoneStudy = (symbol, interval = "1h", signal) =>
  API.get(`/markets/${encodeURIComponent(symbol)}/zones/study`, { params: { interval }, signal });
export const backtestZones = (symbol, interval = "1day", points = 500, minScore = 50) =>
  API.post(`/markets/${encodeURIComponent(symbol)}/zones/backtest`, null, { params: { interval, points, min_score: minScore } });
export const checkZoneAlerts = (interval = "15min") =>
  API.post("/markets/zones/check-alerts", null, { params: { interval } });
export const getMarketProviderUsage = () => API.get("/markets/admin/provider-usage");
export const getAiProviderStatus = () => API.get("/ai/providers");
export const releaseTradingLock = userId => API.post(`/admin/user/${userId}/release-trading-lock`);
export const getUserStats = () => API.get("/user/stats");
export const addTrade = payload => API.post("/trades/add_trade", payload);
export const getTrades = (params = {}) => API.get("/trades/", { params });
export const getCommunityPosts = (params = {}) => API.get("/community/posts", { params });
export const createCommunityPost = (content, imageUrl = null) => API.post("/community/posts", { content, image_url: imageUrl });
export const getPost = id => API.get(`/community/posts/${id}`);
export const updatePost = (id, payload) => API.put(`/community/posts/${id}`, payload);
export const deletePost = id => API.delete(`/community/posts/${id}`);
export const likePost = id => API.post(`/community/posts/${id}/like`);
export const unlikePost = id => API.delete(`/community/posts/${id}/like`);
export const addComment = (postId, content) => API.post(`/community/posts/${postId}/comments`, { content });
export const getComments = (postId, params = {}) => API.get(`/community/posts/${postId}/comments`, { params });
export const deleteComment = commentId => API.delete(`/comments/${commentId}`);
export const reportPost = (id, reason) => API.post(`/community/posts/${id}/report`, { reason });
export const savePost = id => API.post(`/community/posts/${id}/save`);
export const unsavePost = id => API.delete(`/community/posts/${id}/save`);
export const repostPost = id => API.post(`/community/posts/${id}/repost`);
export const followUser = id => API.post(`/community/follow/${id}`);
export const unfollowUser = id => API.delete(`/community/follow/${id}`);
export const getUserFollowers = (id, params = {}) => API.get(`/community/followers/${id}`, { params });
export const getUserFollowing = (id, params = {}) => API.get(`/community/following/${id}`, { params });
export const getUserFeed = (params = {}) => API.get("/community/feed", { params });
export const searchUsers = (query, params = {}) => API.get("/community/search/users", { params: { q: query, ...params } });
export const getTrendingPosts = (params = {}) => API.get("/community/trending", { params });
export const getNotifications = () => API.get("/notifications/");
export const getUserProfile = id => API.get(`/user/${id}`);
export const updateUserProfile = payload => API.put("/user/profile", payload);
export const updateUserAvatar = formData => API.post("/user/avatar", formData, { headers: { "Content-Type": "multipart/form-data" } });
export const getUserSavedPosts = (params = {}) => API.get("/user/saved", { params });
export const getUserLikedPosts = (params = {}) => API.get("/user/liked", { params });
export const getOnboardingStatus = () => API.get("/user/onboarding-status");
export const completeTutorial = tutorialType => API.patch("/user/onboarding-status", { tutorial: tutorialType });

const wsState = { socket: null, reconnectDelay: 1000, manuallyClosed: false, listeners: new Map(), messageHandlers: new Set(), timeout: null };
function emit(event, data) {
  wsState.listeners.get(event)?.forEach(fn => { try { fn(data); } catch (err) { console.error(err); } });
}
function buildWsUrl() {
  const token = localStorage.getItem("token") || "";
  return `${WS_BASE}?token=${encodeURIComponent(token)}`;
}
function openSocket(url) {
  const socket = new WebSocket(url);
  wsState.socket = socket;
  socket.onopen = event => { wsState.reconnectDelay = 1000; emit("open", event); };
  socket.onmessage = event => {
    let payload;
    try { payload = JSON.parse(event.data); } catch { payload = event.data; }
    emit("message", payload);
    wsState.messageHandlers.forEach(fn => fn(payload));
  };
  socket.onclose = event => {
    wsState.socket = null;
    emit("close", event);
    if (!wsState.manuallyClosed) scheduleReconnect();
  };
  socket.onerror = event => emit("error", event);
}
function scheduleReconnect() {
  if (wsState.manuallyClosed) return;
  const delay = Math.min(wsState.reconnectDelay, 30000);
  wsState.timeout = setTimeout(() => {
    openSocket(buildWsUrl());
    wsState.reconnectDelay *= 1.8;
  }, delay);
}
export function connectWS() {
  if (wsState.socket?.readyState === WebSocket.OPEN) return;
  wsState.manuallyClosed = false;
  openSocket(buildWsUrl());
}
export function closeWS() {
  wsState.manuallyClosed = true;
  clearTimeout(wsState.timeout);
  wsState.socket?.close(1000, "client_close");
  wsState.socket = null;
}
export function onWS(event, handler) {
  if (!wsState.listeners.has(event)) wsState.listeners.set(event, new Set());
  wsState.listeners.get(event).add(handler);
  return () => wsState.listeners.get(event)?.delete(handler);
}
export function onWSMessage(handler) {
  wsState.messageHandlers.add(handler);
  return () => wsState.messageHandlers.delete(handler);
}
export function sendWSMessage(data) {
  if (wsState.socket?.readyState !== WebSocket.OPEN) return false;
  wsState.socket.send(JSON.stringify(data));
  return true;
}
export function ensureWSConnected() { if (localStorage.getItem("token")) connectWS(); }

export { API, API_BASE, WS_BASE };
export default API;
